import React, { FC, useState } from 'react';
import { Button, Checkbox, Form, FormInstance, Space, TimePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, last } from 'lodash';
import moment from 'moment';
import { DeleteOutlined } from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import { DayName, Location } from '~/types';
import { useFirstDayOfWeek } from '~/hooks/useHandbook';

interface Props {
  initialValues?: Location;
  formState?: Location;
  form: FormInstance<Location>;
}

const WorkingHoursInput: FC<Props> = (props) => {
  const { initialValues, formState, form } = props;
  const { data: firstDayOfWeek } = useFirstDayOfWeek();
  return (
    <Space direction="vertical" size="middle" style={fullWidth}>
      {getDaysOfWeek(firstDayOfWeek).map((item) => {
        return (
          <Day
            key={item}
            day={item}
            defaultChecked={!!initialValues?.schedule?.[item]?.workingHours}
            workingHours={formState?.schedule?.[item]?.workingHours}
            form={form}
          />
        );
      })}
    </Space>
  );
};

interface DayProps {
  day: DayName;
  defaultChecked?: boolean;
  workingHours?: {
    from: string;
    to: string;
  }[];
  form: FormInstance<Location>;
}

const Day: FC<DayProps> = (props) => {
  const { day, defaultChecked, workingHours, form } = props;
  const [t] = useTranslation('locations');
  const namePath = ['schedule', day, 'workingHours'];

  const [isChecked, setChecked] = useState(defaultChecked);
  const handleChecked = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    if (checked) {
      setChecked(true);
      form.setFields([{ name: namePath, value: [{ from: undefined, to: undefined }] }]);
    } else {
      setChecked(false);
      form.setFields([{ name: ['schedule', day], value: undefined }]);
    }
  };

  const required = {
    validator: async (_: any, value: any) => {
      if (isEmpty(value)) return Promise.reject(new Error(t('translation:required')));
      return Promise.resolve();
    },
  };

  return (
    <Space direction="vertical" size="middle" style={fullWidth}>
      <Checkbox checked={isChecked} onChange={handleChecked}>
        {t(`translation:${day}`)}
      </Checkbox>
      {isChecked ? (
        <div>
          <Form.List name={namePath}>
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map((field) => {
                    return (
                      <Space key={field.key} align="start" style={fullWidth}>
                        <Form.Item {...field} preserve={false} rules={[required]}>
                          <TimeRange workingHours={workingHours} index={field.name} />
                        </Form.Item>
                        <Button
                          type="dashed"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          disabled={field.name === 0}
                        />
                      </Space>
                    );
                  })}
                  <div style={rightSpacing}>
                    <Button type="dashed" onClick={() => add()} block>
                      {t('Add time range')}
                    </Button>
                  </div>
                </>
              );
            }}
          </Form.List>
        </div>
      ) : (
        <Form.Item name={['schedule', day]} initialValue={undefined} noStyle />
      )}
    </Space>
  );
};

interface TimeRangeProps {
  workingHours?: { from: string; to: string }[];
  value?: { from: string; to: string };
  [implicitProps: string]: any;
}

const TimeRange: FC<TimeRangeProps> = (props) => {
  const { value, onChange, workingHours, index, ...rest } = props;
  const [disabledHours = [], disabledMinutesMap = {}] = getDisabledTime(index, workingHours);

  return (
    <TimePicker.RangePicker
      {...rest}
      value={value ? ([parseToValue(value.from), parseToValue(value.to)] as any) : undefined}
      onChange={(item) => {
        const from = item?.[0];
        const to = item?.[1];
        onChange({ from: from?.format('HH:mm'), to: to?.format('HH:mm') });
      }}
      format="HH:mm"
      disabledHours={() => disabledHours}
      disabledMinutes={(h) => disabledMinutesMap[h] || []}
    />
  );
};

const parseToValue = (hhMm?: string) => {
  if (!hhMm) return undefined;
  const hours = Number(hhMm.split(':')[0]);
  const minutes = Number(hhMm.split(':')[1]);
  return moment().set('hours', hours).set('minutes', minutes);
};

export const getDaysOfWeek = (firstDayOfWeek: 'MO' | 'SU' | undefined): DayName[] => {
  const [su, weekdays, sa] = [['SU'], ['MO', 'TU', 'WE', 'TH', 'FR'], ['SA']] as const;
  return firstDayOfWeek === 'MO' ? [...weekdays, ...sa, ...su] : [...su, ...weekdays, ...sa];
};

const getDisabledTime = (
  index: number,
  workingHours?: {
    from: string;
    to: string;
  }[]
): [number[], Record<number, number[]>] => {
  if (!workingHours) return [[], {}];
  const timeRanges: [string | undefined, string | undefined][] = workingHours
    .filter(Boolean)
    .map(({ from, to }: TimeRangeType) => [from, to]);
  const prevRanges = timeRanges.slice(0, index);
  const futureRanges = timeRanges.slice(index + 1);

  const minFromTime = last(prevRanges.flat(1).filter(Boolean));
  const maxToTime = first(futureRanges.flat(1).filter(Boolean));

  const [disabledPastHouts, disabledPastMinutes] = getDisabledPast(minFromTime);
  const [disabledFutureHouts, disabledFutureMinutes] = getDisabledFuture(maxToTime);

  return [
    disabledPastHouts.concat(disabledFutureHouts),
    { ...disabledPastMinutes, ...disabledFutureMinutes },
  ];
};

// time in format HH:mm
const getDisabledPast = (time?: string): [number[], Record<number, number[]>] => {
  if (!time) return [[], {}];
  const disabledHours: number[] = [];
  const disabledMinutes: Record<number, number[]> = {};

  const toHours = Number(time.split(':')[0]);
  const toMinutes = Number(time.split(':')[1]);

  for (let i = 0; i <= toHours; i++) {
    const isLast = toHours === i;

    if (!isLast) {
      disabledHours.push(i);
    }

    disabledMinutes[i] = isLast
      ? new Array(toMinutes + 1).fill('').map((_, index) => index)
      : new Array(60).fill('').map((_, index) => index);
  }
  return [disabledHours, disabledMinutes];
};

// time in format HH:mm
const getDisabledFuture = (time?: string): [number[], Record<number, number[]>] => {
  if (!time) return [[], {}];
  const disabledHours: number[] = [];
  const disabledMinutes: Record<number, number[]> = {};

  const fromHours = Number(time.split(':')[0]);
  const fromMinutes = Number(time.split(':')[1]);

  for (let i = fromHours; i <= 24; i++) {
    const isFirst = i === fromHours;

    if (!isFirst) {
      disabledHours.push(i);
    }

    disabledMinutes[i] = isFirst
      ? new Array(60 - fromMinutes).fill('').map((_, index) => fromMinutes + index)
      : new Array(60).fill('').map((_, index) => index);
  }
  return [disabledHours, disabledMinutes];
};

type TimeRangeType = { from: string | undefined; to: string | undefined };

const fullWidth = { width: '100%' };
const rightSpacing = { marginRight: 8 };

export default WorkingHoursInput;
